import SteppedProgressBar from '@rio-cloud/rio-uikit/SteppedProgressBar';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';

import { useOrderCards } from '@/api/cardOrders';
import { type ChargingCardsSchemaType, ChargingCardsStep } from '@/components/steps/ChargingCardsStep';
import ShippingAddressStep, {
    type ShippingSchemaType,
} from '@/components/steps/ShippingAddressStep/ShippingAddressStep';
import SummaryStep from '@/components/steps/SummaryStep/SummaryStep';
import { OrderComplete } from '@/features/orderNewCards/components/OrderComplete';
import { OrderError } from '@/features/orderNewCards/components/OrderError';
import type { StepConfig, StepperProps } from '@/features/orderNewCards/types';
import { useStepperNavigation } from '@/hooks/useStepperNavigation';
import { t, translate } from '@/lib/utils';
import { ADMINISTRATION_HISTORY } from '@/routes/Router';
import { useUserStore } from '@/stores/userStore';

import { trackOrderAbandon, trackOrderComplete, trackOrderError, trackOrderStep } from '../utils/analytics';

export const Stepper = ({ source, onStepChange }: StepperProps) => {
    const navigate = useNavigate();
    const { countryLocale, countryIntl, account } = useUserStore.getState();
    const orderCardsMutation = useOrderCards();

    const [currentStep, setCurrentStep] = useState(0);
    const [disableFollowingPages, setDisableFollowingPages] = useState<number | undefined>(undefined);
    const [orderStatus, setOrderStatus] = useState<'pending' | 'completed' | 'error'>('pending');

    const [chargingCardsState, setChargingCardsState] = useState<ChargingCardsSchemaType>({
        cardsNumber: 1,
        cardsListCustomization: [{ line1: '', line2: '' }],
    });
    const [shippingAddress, setShippingAddress] = useState<ShippingSchemaType>();

    const { handleNext, handlePrevious, handleStepSelect } = useStepperNavigation(
        setCurrentStep,
        setDisableFollowingPages
    );

    const STEPS: StepConfig[] = [
        {
            name: 'charging_cards',
            label: 'general.chargingCards',
            component: ChargingCardsStep,
        },
        {
            name: 'shipping_address',
            label: 'general.shippingAddress',
            component: ShippingAddressStep,
        },
        {
            name: 'summary',
            label: 'onboarding.stepper.summary',
            component: SummaryStep,
        },
    ];

    const trackStep = useCallback((step: number) => {
        trackOrderStep(step + 1, STEPS[step].name, 'view', source);
    }, []);

    const handleChargingCardState = useCallback(
        (state: ChargingCardsSchemaType) => {
            setChargingCardsState(state);
            setDisableFollowingPages(1);
        },
        [currentStep]
    );

    const handleShippingAddress = useCallback(
        (state: ShippingSchemaType) => {
            setShippingAddress(state);
            setDisableFollowingPages(2);
        },
        [currentStep]
    );

    const handleOrder = useCallback(async () => {
        try {
            await orderCardsMutation.mutateAsync({
                chargingCards: chargingCardsState,
                shipping: shippingAddress,
                countryLocale,
                supportLabel: countryIntl
                    ? translate('cardDetails.supportLabel', countryIntl)
                    : t('cardDetails.supportLabel'),
                supportPhone: countryIntl
                    ? translate('cardDetails.supportPhone', countryIntl)
                    : t('cardDetails.supportPhone'),
            });

            trackOrderComplete(
                chargingCardsState.cardsNumber,
                chargingCardsState.cardsListCustomization.some(card => card.line1 || card.line2)
            );
            setOrderStatus('completed');
        } catch {
            trackOrderError(currentStep, 'api_failure');
            setOrderStatus('error');
        }
    }, [orderCardsMutation, chargingCardsState, shippingAddress, countryLocale, countryIntl, currentStep]);

    const handleGoToChargingCards = useCallback(() => {
        navigate(ADMINISTRATION_HISTORY);
    }, [navigate]);

    const handleTryAgain = useCallback(() => {
        setOrderStatus('pending');
        setCurrentStep(STEPS.length - 1);
    }, []);

    useEffect(() => {
        const handleEscapeKey = () => {
            if (orderStatus === 'pending') {
                trackOrderAbandon(currentStep, STEPS[currentStep].name, source);
            }
        };

        window.addEventListener('keydown', handleEscapeKey);
        return () => window.removeEventListener('keydown', handleEscapeKey);
    }, [currentStep, orderStatus, source]);

    useEffect(() => {
        if (orderStatus === 'pending') {
            trackStep(currentStep);
        }
    }, [currentStep, orderStatus, trackStep]);

    useEffect(() => {
        onStepChange({
            currentStep,
            stepName: STEPS[currentStep].name,
            orderStatus,
        });
    }, [currentStep, orderStatus, onStepChange]);

    useEffect(() => {
        if (account && !shippingAddress) {
            setShippingAddress({
                address: account.address.firstAddressLine ?? '',
                address2: account.address.secondAddressLine ?? '',
                country: account.address.countryCode ?? '',
                postCode: account.address.zip ?? '',
                city: account.address.city ?? '',
                addressee: '',
                addressType: 'company',
            });
        }
    }, [account, shippingAddress]);

    const getStepIcon = useCallback(
        (step: number) =>
            step < currentStep ? (
                <i className='rioglyph rioglyph-ok' />
            ) : (
                <div className='padding-bottom-3 padding-right-1'>{step + 1}</div>
            ),
        [currentStep]
    );

    const getStepProps = () => {
        // Charging cards step
        if (currentStep === 0) {
            return {
                state: chargingCardsState,
                setState: handleChargingCardState,
            };
        }

        // Shipping address step
        if (currentStep === 1) {
            return {
                state: shippingAddress,
                setState: handleShippingAddress,
            };
        }

        // Summary step
        if (currentStep === 2) {
            return {
                chargingCardsState,
                shippingAddressState: shippingAddress,
                setCallback: handleOrder,
                onClickPrevious: handlePrevious,
                goToPage: handleStepSelect,
                isLoading: orderCardsMutation.isLoading,
            };
        }

        return {};
    };

    const renderCurrentStep = () => {
        const StepComponent = STEPS[currentStep].component;

        return (
            <StepComponent
                {...getStepProps()}
                handleNext={handleNext}
                handleBack={currentStep > 0 ? handlePrevious : undefined}
                isOrderNewCard
            />
        );
    };

    if (orderStatus === 'error') {
        return <OrderError tryAgain={handleTryAgain} />;
    }

    if (orderStatus === 'completed') {
        return <OrderComplete onClose={handleGoToChargingCards} />;
    }

    return (
        <div className='margin-50'>
            <div className='margin-bottom-25 padding-bottom-10 margin-top-50 margin-x-20'>
                <SteppedProgressBar
                    variant='circle'
                    selectedStepNumber={currentStep}
                    onSelectedChanged={handleStepSelect}
                    disableFollowingPages={disableFollowingPages ?? currentStep}
                    mobileBreakpoint={false}
                    labels={STEPS.map((step, index) => ({
                        icon: getStepIcon(index),
                        label: <FormattedMessage id={step.label} />,
                    }))}
                />
            </div>
            {renderCurrentStep()}
        </div>
    );
};
